<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard id="campaign-new" class="mb-8" :loading="savingCampaign || savingCampaignPersonalization">
    <template #header>
      <ContentCardSubheader @back="onCancel">
        {{ t('Reviews.product.campaign.new.title', { productName: product.title }) }}
      </ContentCardSubheader>
    </template>
    <div class="py-6">
      <ReviewsProductCampaignNewForm ref="campaignForm" :initial-data="detailsFormData" :product="product" @change="onCampaignChange" />
      <div class="mb-6 txt-headline-xl text-main">{{ t('Reviews.product.campaign.form.inviteMessage.label') }}</div>
      <ReviewsProductCampaignPersonalizationForm
        ref="personalizationForm"
        :campaign="detailsFormData"
        :initial-data="personalisationFormData"
        :product="product"
        :external-locale="currentLocale"
        @change="onPersonalisationChange"
      />
    </div>
    <template #footer>
      <div class="flex flex-row items-center justify-end">
        <button class="btn-primary-color-s" data-testid="reviews-campaign-new-submit" :disabled="isSubmitting" @click="onSubmit">
          {{ t('Reviews.product.action.generateCampaign') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>
<script setup lang="ts">
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { formatDate } from 'date-fns'
import { ref, reactive, useTemplateRef, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs.ts'
import {
  type CampaignAttributes,
  IncentivizationCampaignIncentivizationTypeEnum,
  IncentivizationCampaignLimitTypeEnum,
  IncentivizationCampaignPersonalizationLocaleEnum,
  mapReviewsServerErrors,
  type ProductData,
  useCreateReviewsCampaignMutation,
  useCreateReviewsCampaignPersonalizationMutation,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper.ts'
import { ReviewsRouteName } from '@/router/types.ts'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import ReviewsProductCampaignNewForm from './ReviewsProductCampaignNewForm.vue'
import ReviewsProductCampaignPersonalizationForm from './ReviewsProductCampaignPersonalizationForm.vue'
import type { CampaignFormData, CampaignPersonalizationFormData } from './types.ts'

const props = defineProps<{
  product: ProductData
}>()

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const breadcrumbs = useReviewsProductBreadcrumbs(props.product)
const confirmCloseModal = useTemplateRef('confirmCloseModal')

const campaignForm = useTemplateRef<InstanceType<typeof ReviewsProductCampaignNewForm>>('campaignForm')
const personalizationForm = useTemplateRef<InstanceType<typeof ReviewsProductCampaignPersonalizationForm>>('personalizationForm')

const {
  mutate: createCampaign,
  loading: savingCampaign,
  onError: onCampaignCreateError,
} = useCreateReviewsCampaignMutation({ clientId: 'reviews' })
const {
  mutate: createCampaignPersonalization,
  loading: savingCampaignPersonalization,
  onError: onCampaignPersonalizationCreateError,
} = useCreateReviewsCampaignPersonalizationMutation({ clientId: 'reviews' })

const backTo = { name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns, params: { productId: props.product.id } }

const campaignId = ref<string | undefined>()
const isSubmitting = ref(false)

const CAMPAIGN_OCCASIONS: Record<string, () => string> = {
  top100: () => t('Reviews.product.campaign.new.occasion.top100', { year: new Date().getFullYear() + 1 }),
}

const occasionKey = String(route.query.occasion ?? '')
const occasionTitle = Object.prototype.hasOwnProperty.call(CAMPAIGN_OCCASIONS, occasionKey) ? CAMPAIGN_OCCASIONS[occasionKey]() : ''

const detailsFormData = reactive<CampaignFormData>({
  title: occasionTitle,
  locale: locale.value as IncentivizationCampaignPersonalizationLocaleEnum,
  validFrom: formatDate(new Date(), 'yyyy-MM-dd'),
  validTo: undefined,
  incentive: false,
  incentivizationType: undefined,
  amount: undefined,
  limitType: IncentivizationCampaignLimitTypeEnum.Budget,
  incentivizationsLimit: undefined,
  incentivizationsBudgetLimit: undefined,
})

const personalisationFormData = reactive<CampaignPersonalizationFormData>({
  locale: locale.value as IncentivizationCampaignPersonalizationLocaleEnum,
  inviteMessage: '',
})

// Single source of truth for locale
const currentLocale = ref<IncentivizationCampaignPersonalizationLocaleEnum>(
  locale.value as IncentivizationCampaignPersonalizationLocaleEnum,
)

const v$ = useVuelidate()

const isFormValid = computed((): boolean => {
  return !!(campaignForm.value?.isValid && personalizationForm.value?.isValid)
})

function onCampaignChange(data: CampaignFormData) {
  Object.assign(detailsFormData, data)
}

function onPersonalisationChange(data: CampaignPersonalizationFormData) {
  Object.assign(personalisationFormData, data)
}

function onCancel() {
  track('campaign_create_cancel', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id })

  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

async function onSubmit() {
  track('campaign_publish', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id })

  if (isSubmitting.value) return

  campaignForm.value?.clearExternalErrors()
  personalizationForm.value?.clearExternalErrors()

  await campaignForm.value?.validate()
  await personalizationForm.value?.validate()

  if (!isFormValid.value) {
    showNotification(t('Reviews.product.notifications.invalidData'), 'error')
    return
  }

  isSubmitting.value = true

  if (!campaignId.value) {
    const campaignAttrs: CampaignAttributes = {
      amount: detailsFormData.amount,
      validFrom: detailsFormData.validFrom,
      validTo: detailsFormData.validTo,
      title: detailsFormData.title,
    }

    if (detailsFormData.incentive) {
      campaignAttrs.incentivizationType = detailsFormData.incentivizationType
    }

    if (detailsFormData.incentive && detailsFormData.incentivizationType !== IncentivizationCampaignIncentivizationTypeEnum.None) {
      campaignAttrs.limitType = detailsFormData.limitType

      if (detailsFormData.limitType === IncentivizationCampaignLimitTypeEnum.Budget) {
        campaignAttrs.incentivizationsLimit = Math.floor((detailsFormData.incentivizationsBudgetLimit || 0) / (detailsFormData.amount || 0))
      } else {
        campaignAttrs.incentivizationsLimit = detailsFormData.incentivizationsLimit
      }
    }

    const campaignResponse = await createCampaign({
      productId: props.product.id,
      attributes: campaignAttrs,
    })

    const campaign = campaignResponse?.data?.manage.createCampaign
    if (campaign?.errors && campaign.errors.length > 0) {
      campaignForm.value?.updateExternalErrors(mapReviewsServerErrors(campaign.errors))
      isSubmitting.value = false
      return
    }

    campaignId.value = campaign?.id || undefined
  }

  if (campaignId.value) {
    const personalizationResponse = await createCampaignPersonalization({
      campaign: campaignId.value,
      attributes: {
        locale: personalisationFormData.locale,
        inviteMessage: personalisationFormData.inviteMessage,
      },
    })

    const personalization = personalizationResponse?.data?.manage.createCampaignPersonalization
    if (personalization?.errors && personalization.errors.length > 0) {
      personalizationForm.value?.updateExternalErrors(mapReviewsServerErrors(personalization.errors))
      isSubmitting.value = false
      return
    }

    showNotification(t('Reviews.product.campaign.new.successMessage'), 'success')
    router.push({ name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns, params: { productId: props.product.id } })
  }

  isSubmitting.value = false
}

// Sync locale between forms
watch(
  () => detailsFormData.locale,
  (newLocale) => {
    currentLocale.value = newLocale
    personalisationFormData.locale = newLocale
  },
)

onCampaignCreateError((error) => {
  showNotification(error.message, 'error')
  isSubmitting.value = false
})

onCampaignPersonalizationCreateError((error) => {
  showNotification(error.message, 'error')
  isSubmitting.value = false
})
</script>
